import { EmployeeUser, JobTitle } from './admin_types';
import { Assessment } from './assessment_types';

export type jobOpeningTypes =
  | 'Job Opening Details'
  | 'Job Posting'
  | 'Applicant Analysis'
  | 'Auto-Suitability'
  | 'Auto-Outstanding'
  | 'Auto-Assessments'
  | 'Smart Interviews'
  | 'Analytics'
  | 'Psychometric'
  | 'Wellness';
export type jobOpeningDetailsTabs =
  | 'Job Requisition'
  | 'Job Description'
  | 'Job Posting'
  | 'CV Ingestion'
  | 'Auto Suitability'
  | 'Auto Outstanding'
  | 'Auto Assess Questions'
  | 'CCA Questions'
  | 'Permissions';
export type InterviewTabs = 'Round One' | 'Round Two' | 'Add Round';
export type jobOpeningPages =
  | 'Job Opening Details'
  | 'Applicant Analysis'
  | 'Auto-Assessments'
  | 'Smart Interviews'
  | 'Analytics'
  | 'Psychometric'
  | 'Wellness';
export type applicantScopeType = 'Internal' | 'External';
export type workType = 'Permanent' | 'Part Time' | 'Casual' | 'Contract';
export type workArrangement = 'Hybrid' | 'Remote' | 'Office';

export type showAllTypes = 'showAll' | 'collapseAll' | 'random';
export type JobReferenceNumberTypes = 'Auto' | 'Manual' | 'JD';
export const AutoBuildCompleteStatus = 'Generated Questions for all skills';
export type LocationType = 'remote' | 'hybrid' | 'onsite';

export type DocumentRequirementStatus = 'Mandatory' | 'Optional' | 'Disabled';

export type DocumentConfiguration = {
  type: string;
  requirement: DocumentRequirementStatus;
  description?: string;
  allowMultiple?: boolean;
};
export interface IDocumentRequirement {
  label: string;
  isMandatory: boolean;
  allowMultiple: boolean;
  acceptedFileTypes: string[];
  description: string;
  key: string;
}

export interface IDocumentRequirementItem extends IDocumentRequirement {
  key: string;
}
export type JobRequisition = {
  id: string;
  jobRequisitionType: 'New Role' | 'Replacement';
  applicantScope: applicantScopeType[];
  jobOpeningId: string;
  hiringManager: string[] | EmployeeUser[];
  deadline: number | string;
  internalRecruiter: string[] | EmployeeUser[];
  headCount: number;
  tags?: string[];
  documentConfigurations?: DocumentConfiguration[];
};
export interface IAiScoreWeights {
  location?: number;
  minimumWorkExperience?: number;
  mandatorySkills?: number;
  desirableSkills?: number;
  keyword?: number;
  behavioralAttributes?: number;
}
export interface JobDescription {
  id: string;
  jobTitle?: JobTitle | string;
  filename?: string;
  jobOpeningId: string;
  jobReferenceNumber?: string;
  jobReferenceNumberJD?: string;
  jobLocation?: string;
  locationType?: LocationType;
  city?: string;
  state?: string;
  country?: string;
  workType?: workType;
  responsibilities?: string[];
  educations?: string[];
  certifications?: string[];
  mandatorySkills?: string[];
  behavioralAttributes?: string[];
  desirableSkills?: string[];
  status?: string;
  errorMessage?: string;
  transcript?: string;
  completed?: boolean;
  minimumWorkExperience?: number;
  jobReferenceType: JobReferenceNumberTypes;
  jobReferenceNumberAuto: string;
  aiScoreWeights?: IAiScoreWeights;
  documentRequirements?: IDocumentRequirement[];
}

export interface JobDescriptionTemplate {
  templateTitle: string;
  templateDescription: string;
  jobTitle?: JobTitle | string;
  jobLocation?: string;
  tags?: string[];
  workType?: workType;
  responsibilities?: string[];
  educations?: string[];
  certifications?: string[];
  mandatorySkills?: string[];
  behavioralAttributes?: string[];
  desirableSkills?: string[];
  minimumWorkExperience?: number;
  aiScoreWeights?: IAiScoreWeights;
  documentRequirements?: IDocumentRequirement[];
}

export type FilterOption = {
  value: string;
  label: string;
};

export type JobPostingTabData = {
  id: string;
  jobOpeningId: string;
  organizationId: string;
  jobTitle: string;
  location: string;
  department: string;
  workType: workArrangement;
  htmlTemplate?: string;
  jsonTemplate?: string;
  active: boolean;
  startDate: string;
  endDate: string;
  enableVideo?: boolean;
  videoFilename?: File | string;
  videoTitle?: string;
  videoDescription?: string;
  documentConfigurations?: DocumentConfiguration[];
  ccaEnabled?: boolean;
  ccaExpiryDate?: string;
  language?: string;
  availableLanguages?: string[];
};

export type AutoEvaluationLabel = {
  value: string;
  relationToNext: 'AND' | 'OR';
};

export type AutoEvaluationLabelWithNumber = {
  value: number;
  operator: 'Greater Than' | 'Lesser Than';
};

export type AutoEvaluation = {
  id: string;
  jobOpeningId: string;
  jobLocation: string;
  age: AutoEvaluationLabelWithNumber;
  workExperience: AutoEvaluationLabelWithNumber;
  educations: AutoEvaluationLabel[];
  certifications: AutoEvaluationLabel[];
  skills: AutoEvaluationLabel[];
  school: AutoEvaluationLabel[];
  keywords: AutoEvaluationLabel[];
  desirableSkills?: AutoEvaluationLabel[];
  behavioralAttributes?: AutoEvaluationLabel[];
};

export type Status = {
  jobDescription: string;
  jobPost: string;
  shortlisting: string;
  interviews: string;
  decisionMaking: string;
  closed: string;
};

export type JobOpeningDetail = {
  creationDateTime: number | string;
  organizationId: string;
  jobOpeningElements: jobOpeningTypes[];
  jobRequisition?: JobRequisition;
  jobDescription?: JobDescription;
  jobPosting?: JobPostingTabData;
  autoSuitability?: AutoEvaluation;
  autoOutstanding?: AutoEvaluation;
  autoAssessment?: AutoAssessment;
  status?: Status;
  questionAdded?: boolean;
  applicants?: number;
  CVIngestion?: boolean;
};

export type AutoAssessment = {
  id: string;
  jobOpeningId: string;
  assessment: Assessment;
  skills?: JobOpeningSkill[];
  status?: string;
  emailTemplate?: string;
  emailJsonTemplate?: string;
};

export type SocialLinks = {
  github?: string | null;
  linkedin?: string | null;
  personalWebsite?: string | null;
};

export type ApplicantDocument = {
  jobApplicantId: string;
  jobOpeningId: string;
  requirementKey: string;
  requirementLabel: string;
  s3Filename: string;
  baseFilename: string;
  fileHash: string;
  uploadedAt: number;
  id: string;
};

export type JobApplicant = {
  jobOpeningId: string;
  organizationId: string;
  baseFilename?: string;
  filename?: string;
  createdAt: number | string;
  documentType?: 'Resume' | 'Cover Letter';
  fileHash?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  status?: string;
  gender?: string | null;
  applicantStatus: string;
  errorMessage?: string;
  transcript?: string;
  completed: boolean;
  profileImageFilename?: string;
  base64Encoded?: string;
  contactInfo?: ContactInfo;
  educations?: Education[];
  workExperience?: WorkExperience[];
  skills?: string[];
  certifications?: string[];
  notes?: string;
  id?: string;
  pdfUrl?: string;
  highlightSkills?: HighlightSkill[][];
  highlightWorkExperience?: any[];
  socials?: SocialLinks;
  behavioralAttributes?: string[];
  conversationNotes?: ConversationNote[];
  applicantDocuments?: ApplicantDocument[];
  userId?: string;
  preQualSentAt?: number | string;
  preQualStatus?: string;
  jobSeekingStatus?: string;
  statusHistory?: StatusHistoryItem[];
  aiScore?: number;
};

export type ContactInfo = {
  phone: string;
  address: Address;
};

export type Address = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type Education = {
  degree: string;
  fieldOfStudy: string;
  university: string;
  graduationYear: number;
};

export type WorkExperience = {
  jobTitle: string;
  company: string;
  startDate: number;
  endDate?: number;
  description: string;
};

export type HighlightSkill = {
  value: string;
  type: string;
};

export interface Author {
  email: string;
  id: string;
  firstName: string;
  lastName: string;
}

export type ConversationNote = {
  id: string;
  noteBody: string;
  noteSubject: string;
  noteType: string;
  createdAt: string;
  author: Author;
};

export type NoteStructure = {
  noteBody: string;
  noteSubject: string;
  noteType: string;
  createdAt?: string;
};

export type PreQualCondition = {
  question_key: string;
  choice?: { values: string[] };
  number?: { min?: number; max?: number };
  location?: {
    lat?: number;
    lng?: number;
    radius_km?: number;
    city?: string;
    country?: string;
  };
};

export type PreQualFilter = {
  conditions: PreQualCondition[];
};

export interface IGetJobApplicants {
  limit: number;
  page: number;
  search: string;
  status: string;
}
export type JobApplicantSearchRequest = {
  additional_skills: {
    skill_weightage: boolean;
    similar: boolean;
    skills: { skill: string; weight: number }[];
  };
  job_title: {
    similar: boolean;
    job_titles: string[];
  };
  keywords: {
    keyword_weightage: boolean;
    similar: boolean;
    keywords: { keyword: string; weight: number }[];
  };
  behavioral_attributes: {
    similar: boolean;
    behavioral_attributes: string[];
  };
  gender: string[];
  age: {
    min_age: number;
    max_age: number;
    include_unknown: boolean;
  };
  applicant_status: string[] | null;
  candidate_ids?: string[];
  candidateIds?: string[];
  prequal_filter?: PreQualFilter | null;
};

export type CandibankJobApplicantRequest = {
  additionalSkills: {
    skillWeightage: boolean;
    similar: boolean;
    skills: { skill: string; weight: number }[];
  };
  jobTitle: {
    similar: boolean;
    jobTitles: string[];
  };
  keywords: {
    keywordWeightage: boolean;
    similar: boolean;
    keywords: { keyword: string; weight: number }[];
  };
  behavioralAttributes: {
    similar: boolean;
    behavioralAttributes: string[];
  };
  gender: string[];
  age: {
    minAge: number;
    maxAge: number;
    includeUnknown: boolean;
  };
  applicantStatus: string[] | null;
  candidateIds: string[];
};

export type JobApplicantCSV = {
  additional_skills: {
    skill_weightage: boolean;
    similar: boolean;
    skills: { skill: string; weight: number }[];
  };
  job_title: {
    similar: boolean;
    job_titles: string[];
  };
  keywords: {
    keyword_weightage: boolean;
    similar: boolean;
    keywords: { keyword: string; weight: number }[];
  };
  gender: string[];
  age: JobApplicantAge;
  applicant_status: string[] | null;
  skills: JobSkills;
  experience: JobExperience;
  behavioral_attributes: {
    similar: boolean;
    behavioral_attributes: string[];
  };
  prequal_filter?: PreQualFilter | null;
};

export type JobSkills = {
  min: number;
  max: number;
};

export type JobExperience = {
  min: number;
  max: number;
};

export type JobApplicantsStatusRequest = {
  jobApplicantIds: string[];
  applicantStatus: string;
};

export type JobApplicantEditRequest = {
  firstName?: string;
  lastName?: string;
  email?: string;
  age?: number | null;
  skills?: string[];
  gender?: string;
  applicantStatus: string;
};

export type JobApplicantAge = {
  min_age: number;
  max_age: number;
  include_unknown: boolean;
};

export type Applicant = {
  userId: string;
  value: number[];
  symbol: string;
  initials?: string;
  label?: Label;
  name: string;
  skills: string[];
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  gender: string | null;
  matchedSkills: string[];
  aiScore: number;
  applicantStatus: string;
  locationCity: string;
  locationCountry: string;
  behavioralAttributes: string[];
  behavior: string[];
  matchedBehavior: string[];
  keywords?: string[]; // Add keywords field
  experience?: number;
  noOfSkills?: number;
  email?: string;
  pdfUrl?: string;
  base64Encoded?: string;
  contactInfo?: ContactInfo;
  socials?: SocialLinks;
  workExperience?: WorkExperience[];
  educations?: Education[];
  applicantDocuments?: ApplicantDocument[];
  conversationNotes?: ConversationNote[];
  highlightSkills?: HighlightSkill[][];
  status?: string;
  createdAt?: number | string;
  statusHistory?: StatusHistoryItem[];
};

export type StatusHistoryItem = {
  status: string;
  timestamp: number | string;
  actorId?: string | null;
  actorName?: string | null;
  reason?: string | null;
  note?: string | null;
};
export interface ICandidate {
  firstName: string;
  lastName: string;
  email: string;
  id: string;
  interviewRoundId: string;
  interviewStartTime: string;
  interviewType: string;
  meetingLink?: string;
  inPersonAddress?: string;
  interviewApplicantStatus: string;
  hasPanelistNotes?: boolean;
}
export type Label = {
  show: boolean;
  formatter: string;
  color: string;
  fontWeight: string;
};

export type TabData = {
  tabId: number;
  candidate?: Applicant;
};

export type PieItem = {
  value: number;
  name: string;
  color?: string;
};

export type ApplicantResponse = {
  email: string | undefined;
  skillCount: number;
  experience: number;
  behaviorCount: number;
  keywordCount: number;
  skills: string[];
  keyword: string[];
  id: string;
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  gender: string | null;
  profilePic: string | null;
  base64Encoded: string | null;
  matchedSkills: string[];
  aiScore: number;
  applicantStatus: string;
  locationCity: string;
  locationCountry: string;
  behavioralAttributes: string[];
  matchedBehavior: string[];
  skillPercentage: number;
  behavior: string[];
};

export type Coordinate = [number, number];

export type Skill = {
  skill: string;
  weight: number;
  weightPercent?: number;
};

export type SegmentType = {
  id?: number;
  name?: string;
  xAxis?: number;
  yAxis?: number;
  itemStyle?: { color: string | undefined };
};

export type JobOpeningRequest = {
  jobTitle: string;
  jobReferenceNumber: string;
  hiringManager: string;
  internalRecruiter: string;
  tags?: string[]; // Add tags field as optional
  sort: sortJobOpening;
  active: boolean;
};

export type sortJobOpening = {
  creationDate?: 'asc' | 'desc';
  applicantStatus?: 'asc' | 'desc';
  jobTitle?: 'asc' | 'desc';
};

export type sortCandidates = {
  firstName?: 'asc' | 'desc';
  creationDate?: 'asc' | 'desc';
  invited?: 'asc' | 'desc';
  assessmentStatus?: 'asc' | 'desc';
};

export type sortResults = {
  aiOverallScore?: 'asc' | 'desc';
  user?: 'asc' | 'desc';
  interviewStartTime?: 'asc' | 'desc';
  status?: 'asc' | 'desc';
  averageScore?: 'asc' | 'desc';
  technicalSkills?: 'asc' | 'desc';
  roleExperience?: 'asc' | 'desc';
  industrySkills?: 'asc' | 'desc';
  productKnowledge?: 'asc' | 'desc';
  communication?: 'asc' | 'desc';
  problemSolving?: 'asc' | 'desc';
  teamCollaboration?: 'asc' | 'desc';
};

export type JobOpeningSkill = {
  skill: string;
  level: string;
  numberOfQuestions: number;
  points: number;
  time: number;
  questionType: string;
  temp?: boolean;
};

export type JobOpeningList = {
  id: string;
  jobTitle?: string;
  hiringManager?: string[];
  internalRecruiter?: string[];
  creationDate: number;
  expiryDate?: number;
  applicants: number;
  jobReference?: string;
  tags?: string[];
};

export type JobPostingData = {
  location: string;
  jobTitle: string;
  department: string;
  workType: string;
  organizationId: string;
  htmlTemplate: string;
  jsonTemplate: string;
  startDate: number;
  endDate: number;
  active: boolean;
  id: string;
  jobOpeningId?: string;
  jobReferenceNumber?: string;
  videoFilename?: File | string;
  videoTitle?: string;
  videoDescription?: string;
  documentConfigurations?: DocumentConfiguration[];
  documentRequirements?: IDocumentRequirementItem[];
  language?: string;
  availableLanguages?: string[];
};

export type FilterState = {
  jobTitle: string[];
  location: string[];
  workType: string[];
  department: string[];
};

export type CandidateJobApplicantsData = {
  email: string;
  applicantStatus: string[];
  name: string;
  assessmentStatus: string[];
  invited: boolean;
  previousRoundId?: string; // Optional ID of the previous round to get candidates from
  status?: string[]; // Status of candidates to include
};

export type PatchProgress = {
  jobDescription: string;
  jobPost: string;
  shortlisting: string;
  interviews: string;
  decisionMaking: string;
  closed: string;
};

export type JDTemplate = {
  templateId: string;
  templateTitle: string;
  templateDescription: string;
  jobTitle: string;
  createdAt: number;
  tags?: string[];
};

export interface PreQualQuestion {
  questionKey?: string;
  question: string;
  questionType:
    | 'location'
    | 'number'
    | 'multiple-choice'
    | 'text'
    | 'video-answer'
    | 'video';
  choices: string[];
  order: number;
}

export interface PreQualConfig {
  id: string;
  jobOpeningId: string;
  organizationId: string;
  questions: PreQualQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface PreQualAnswers {
  answers: {
    id: string;
    jobApplicantId: string;
    jobOpeningId: string;
    organizationId: string;
    [key: string]: any;
    submittedAt: string;
  } | null;
  preQualSentAt?: number | string;
  questions?: PreQualQuestion[];
  videoAnswers?: {
    questionKey: string;
    videoKey?: string;
    url?: string;
  }[];
  locationAnswers: {
    id: string;
    jobApplicantId: string;
    jobOpeningId: string;
    questionKey: string;
    city: string;
    state: string | null;
    country: string;
    postcode: string;
    unknown: boolean;
  }[];
}

export interface InviteListItem {
  id: string;
  jobApplicantId: string;
  firstName: string;
  lastName: string;
  email: string;
  invited: boolean;
  invitedAt?: number;
  applied: boolean;
  addedAt: number;
}

export interface InviteListResponse {
  page: number;
  pageSize: number;
  total: number;
  results: InviteListItem[];
}

export interface AddToInviteListRequest {
  jobApplicantIds: string[];
}

export interface SendInvitesRequest {
  inviteIds?: string[];
  sendAll?: boolean;
}

export interface SendInvitesByEmailRequest {
  emails: string[];
}
